/**
 * ToastService - Toast notification management service
 * Handles creating, managing, and dismissing toast notifications
 */

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

type ToastListener = (toasts: Toast[]) => void;

class ToastService {
  private toasts: Toast[] = [];
  private listeners: Set<ToastListener> = new Set();
  private nextId = 0;

  /**
   * Subscribe to toast changes
   */
  subscribe(listener: ToastListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of toast changes
   */
  private notify() {
    this.listeners.forEach(listener => listener([...this.toasts]));
  }

  /**
   * Show a toast notification
   */
  show(
    message: string,
    variant: ToastVariant = 'info',
    duration: number = 5000,
    action?: Toast['action']
  ): string {
    const id = String(this.nextId++);
    const toast: Toast = {
      id,
      message,
      variant,
      duration,
      action
    };

    this.toasts.push(toast);
    this.notify();

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => this.dismiss(id), duration);
    }

    return id;
  }

  /**
   * Show success toast
   */
  success(message: string, duration?: number, action?: Toast['action']): string {
    return this.show(message, 'success', duration, action);
  }

  /**
   * Show error toast
   */
  error(message: string, duration?: number, action?: Toast['action']): string {
    return this.show(message, 'error', duration, action);
  }

  /**
   * Show warning toast
   */
  warning(message: string, duration?: number, action?: Toast['action']): string {
    return this.show(message, 'warning', duration, action);
  }

  /**
   * Show info toast
   */
  info(message: string, duration?: number, action?: Toast['action']): string {
    return this.show(message, 'info', duration, action);
  }

  /**
   * Dismiss a toast by ID
   */
  dismiss(id: string) {
    this.toasts = this.toasts.filter(t => t.id !== id);
    this.notify();
  }

  /**
   * Dismiss all toasts
   */
  dismissAll() {
    this.toasts = [];
    this.notify();
  }

  /**
   * Get all current toasts
   */
  getToasts(): Toast[] {
    return [...this.toasts];
  }
}

const toastService = new ToastService();
export default toastService;
